import { useState } from "react";
import { toast } from "sonner";
import { Download, Facebook, Instagram, Mail, MessageCircle, Phone } from "lucide-react";
import { sendEnquiry } from "@/lib/api";
import { ARTIST, ENQUIRY_TYPES, PORTFOLIO_URL, formatApiError } from "@/lib/constants";
import { Bindi, Reveal, SectionHeading } from "@/components/motifs";

const EMPTY = { name: "", email: "", phone: "", enquiry_type: ENQUIRY_TYPES[0], message: "" };

export const ContactChapter = ({ headingAs }) => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await sendEnquiry(form);
      toast.success("Thank you — your enquiry has been received.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const contactRows = [
    { icon: Phone, label: ARTIST.phone, href: ARTIST.phoneHref, testId: "contact-phone-link" },
    { icon: Mail, label: ARTIST.email, href: `mailto:${ARTIST.email}`, testId: "contact-email-link" },
    { icon: MessageCircle, label: "WhatsApp me", href: ARTIST.whatsapp, testId: "contact-whatsapp-link", external: true },
    { icon: Instagram, label: ARTIST.instagram.replace("https://instagram.com/", "@"), href: ARTIST.instagram, testId: "contact-instagram-link", external: true },
    { icon: Facebook, label: ARTIST.facebook.replace("https://facebook.com/", "@"), href: ARTIST.facebook, testId: "contact-facebook-link", external: true },
  ];

  return (
    <section id="contact" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32" data-testid="contact-section">
      <SectionHeading number="07" title="Commissions & Contact" note="Begin a conversation" as={headingAs} />

      <div className="grid gap-16 lg:grid-cols-12">
        <Reveal className="lg:col-span-5">
          <h3 className="font-serif text-2xl font-medium leading-snug tracking-tight sm:text-3xl">
            Commission a painting, invite me to an exhibition, or simply ask about the tradition.
          </h3>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-muted-foreground">
            Commissions, exhibition enquiries, workshops and collaborations are all welcome. Every
            message reaches me directly.
          </p>
          <div className="mt-10 space-y-4">
            {contactRows.map((row) => (
              <a
                key={row.testId}
                href={row.href}
                target={row.external ? "_blank" : undefined}
                rel={row.external ? "noopener noreferrer" : undefined}
                data-testid={row.testId}
                className="group flex items-center gap-4"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-border transition-colors group-hover:border-accent group-hover:text-accent">
                  <row.icon size={16} />
                </span>
                <span className="font-serif text-lg tracking-wide">{row.label}</span>
              </a>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <a
              href={PORTFOLIO_URL}
              data-testid="contact-portfolio-download"
              className="group inline-flex items-center gap-3 rounded-full border border-foreground/30 px-6 py-3 text-[13px] font-semibold tracking-wide transition-colors duration-300 hover:border-accent hover:text-accent"
            >
              <Download size={15} className="transition-transform duration-300 group-hover:translate-y-0.5" />
              Portfolio · English
            </a>
          </div>
          <p className="eyebrow mt-12 flex items-center gap-3 text-muted-foreground">
            <Bindi size={7} /> {ARTIST.location}
          </p>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-7">
          <form onSubmit={submit} className="grid gap-5 sm:grid-cols-2" data-testid="contact-form">
            <input
              required
              value={form.name}
              onChange={set("name")}
              placeholder="Your name"
              data-testid="contact-form-name-input"
              className="input-field"
            />
            <input
              required
              type="email"
              value={form.email}
              onChange={set("email")}
              placeholder="Email address"
              data-testid="contact-form-email-input"
              className="input-field"
            />
            <input
              value={form.phone}
              onChange={set("phone")}
              placeholder="Phone (optional)"
              data-testid="contact-form-phone-input"
              className="input-field"
            />
            <select
              value={form.enquiry_type}
              onChange={set("enquiry_type")}
              data-testid="contact-form-type-select"
              className="input-field bg-background"
            >
              {ENQUIRY_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <textarea
              required
              value={form.message}
              onChange={set("message")}
              placeholder="Tell me about the artwork, occasion or idea you have in mind…"
              rows={5}
              data-testid="contact-form-message-input"
              className="input-field resize-none sm:col-span-2"
            />
            <div className="flex items-center justify-between sm:col-span-2">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Replies are personal — never automated
              </p>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-form-submit-button"
                className="rounded-full bg-foreground px-8 py-3.5 text-[13px] font-semibold tracking-wide text-background transition-colors duration-300 hover:bg-accent hover:text-accent-foreground disabled:opacity-50"
              >
                {sending ? "Sending…" : "Send enquiry"}
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
